import React, { FC, useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Statistic,
  Row,
  Col,
  Progress,
  Timeline,
  Badge,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  TagsOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { phaseApis } from "../../../api/Phase";
import { normsApis } from "../../../api/Norms";
import NormMaterialRollupCard from "../norms/NormMaterialRollupCard";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getColorByLabel } from "../projects/ProjectInformationById";
import { useWbsName } from "../shared/useWbsCodes";

interface PhaseQuickViewCardProps {
  phaseId?: number;
  derivedProjectStatus?: string;
  onClose?: () => void;
  onRefresh?: () => void;
}

const PhaseQuickViewCard: FC<PhaseQuickViewCardProps> = ({
  phaseId: propPhaseId,
  derivedProjectStatus,
  onClose,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(true);
  const [phaseData, setPhaseData] = useState<any>(null);
  const wbsName = useWbsName();

  const { data: PhaseInDetailModal } = useSelector(
    (state: RootState) => state.PhaseInDetailModal
  );
  const phaseId = propPhaseId || PhaseInDetailModal?.id;

  const fetchPhaseData = async () => {
    if (!phaseId) return;
    setLoading(true);
    try {
      const response = await phaseApis.getPhaseById(
        phaseId,
        derivedProjectStatus
      );
      if (response?.status && response?.data) {
        setPhaseData(response.data);
      }
    } catch (error) {
      console.error("Error fetching phase data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhaseData();
  }, [phaseId]);

  // Rolled-up norm material requirement across this phase's tasks.
  const [normMaterials, setNormMaterials] = useState<any[]>([]);
  const [normMatLoading, setNormMatLoading] = useState(false);
  useEffect(() => {
    if (!phaseId) return;
    let active = true;
    (async () => {
      try {
        setNormMatLoading(true);
        const res = await normsApis.getPhaseNormMaterials(Number(phaseId));
        if (active && res?.status === true)
          setNormMaterials(
            Array.isArray(res?.data?.materials) ? res.data.materials : [],
          );
      } catch {
        /* non-fatal */
      } finally {
        if (active) setNormMatLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [phaseId]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!phaseData) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
        No phase data available
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "active" || statusLower === "inprogress") return "processing";
    if (statusLower === "completed" || statusLower === "closed") return "success";
    if (statusLower === "onhold") return "warning";
    if (statusLower === "cancelled" || statusLower === "deleted") return "error";
    if (statusLower === "draft") return "default";
    return "default";
  };

  const completionPercent = parseFloat(phaseData.tasksCompletionPercentage || "0");

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Header Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <h4
            style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#1a1a2e" }}
            title={phaseData.phaseName}
          >
            {wbsName(phaseData.phaseName)}
          </h4>
          <Badge status={getStatusColor(phaseData.status)} text={
            <span style={{ fontWeight: 500, fontSize: 14 }}>{phaseData.status}</span>
          } />
        </div>
        {phaseData.flag && (
          <Tag color={phaseData.flag === "Internal" ? "blue" : "orange"} style={{ marginTop: 4 }}>
            <FlagOutlined /> {phaseData.flag}
          </Tag>
        )}
      </div>

      {/* Progress Section */}
      <Card
        size="small"
        style={{
          marginBottom: 20,
          borderRadius: 12,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <Row gutter={16} align="middle">
          <Col span={12}>
            <Statistic
              title={<span style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>Tasks Completed</span>}
              value={phaseData.completedTasks || 0}
              suffix={<span style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>/ {phaseData.totalTasks || 0}</span>}
              valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 600 }}
            />
          </Col>
          <Col span={12}>
            <div style={{ textAlign: "right" }}>
              <Progress
                type="circle"
                percent={completionPercent}
                size={70}
                strokeColor="#52c41a"
                trailColor="rgba(255,255,255,0.3)"
                format={(percent) => (
                  <span style={{ color: "#fff", fontWeight: 600 }}>{percent?.toFixed(0)}%</span>
                )}
              />
            </div>
          </Col>
        </Row>
        {/* Budget progress: consumed vs planned 3M value of the phase's tasks */}
        {(() => {
          const consumed = Number(phaseData.budgetConsumption) || 0;
          const planned = Number(phaseData.budgetPlanned) || 0;
          const fmt = (n: number) =>
            `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
          const percent =
            planned > 0 ? Math.round((consumed / planned) * 100) : 0;
          return (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}
                >
                  Budget Progress (3M Consumption)
                </span>
                <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
                  {fmt(consumed)}
                  {planned > 0 && (
                    <span
                      style={{ color: "rgba(255,255,255,0.7)", fontWeight: 400 }}
                    >
                      {" "}
                      / {fmt(planned)} ({percent}%)
                    </span>
                  )}
                </span>
              </div>
              <Progress
                percent={
                  planned > 0
                    ? Math.min(100, percent)
                    : consumed > 0
                    ? 100
                    : 0
                }
                showInfo={false}
                strokeLinecap="butt"
                size={["100%", 8]}
                strokeColor={
                  (planned > 0 && percent > 100) ||
                  (planned <= 0 && consumed > 0)
                    ? "#ff4d4f"
                    : "#52c41a"
                }
                trailColor="rgba(255,255,255,0.3)"
              />
            </div>
          );
        })()}
      </Card>

      {/* Quick Stats */}
      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 10, background: "#f0f9ff", border: "1px solid #bae0ff" }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: "#0958d9" }}>Total Activities</span>}
              value={phaseData.totalActivities || 0}
              valueStyle={{ color: "#0958d9", fontSize: 22 }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 10, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: "#389e0d" }}>Completed Activities</span>}
              value={phaseData.completedActivities || 0}
              valueStyle={{ color: "#389e0d", fontSize: 22 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Details Section */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Phase Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Descriptions column={1} size="small" labelStyle={{ fontWeight: 500, color: "#666", width: 140 }}>
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 6 }} />Owner</>}>
            {phaseData.phaseOwnerName || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Start Date</>}>
            {phaseData.phaseStartDate ? dayjs(phaseData.phaseStartDate).format("DD MMM YYYY") : "Not Set"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />End Date</>}>
            {phaseData.phaseEndDate ? dayjs(phaseData.phaseEndDate).format("DD MMM YYYY") : "Not Set"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Actual Start</>}>
            {phaseData.actualStartDate ? dayjs(phaseData.actualStartDate).format("DD MMM YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Actual End</>}>
            {phaseData.actualEndDate ? dayjs(phaseData.actualEndDate).format("DD MMM YYYY") : "N/A"}
          </Descriptions.Item>
          {phaseData.phaseWeightage && (
            <Descriptions.Item label={<><PercentageOutlined style={{ marginRight: 6 }} />Weightage</>}>
              {phaseData.phaseWeightage}%
            </Descriptions.Item>
          )}
          <Descriptions.Item label={<><ClockCircleOutlined style={{ marginRight: 6 }} />Created</>}>
            {phaseData.createdAt ? dayjs(phaseData.createdAt).format("DD MMM YYYY") : "N/A"}
            {phaseData.createdByUserName && (
              <span style={{ color: "#999", marginLeft: 8 }}>by {phaseData.createdByUserName}</span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Materials from Norm — rolled up across this phase's tasks */}
      <NormMaterialRollupCard
        materials={normMaterials}
        loading={normMatLoading}
      />

      {/* Tags Section */}
      {phaseData.phaseTags && phaseData.phaseTags.length > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <TagsOutlined style={{ marginRight: 8 }} />
              Tags
            </span>
          }
          style={{ borderRadius: 12 }}
          headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {phaseData.phaseTags.map((tag: any, index: number) => {
              const color = getColorByLabel(tag.tagName || "");
              return (
                <Tag
                  key={index}
                  style={{
                    backgroundColor: color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 10px",
                  }}
                >
                  {tag.tagName}
                </Tag>
              );
            })}
          </div>
        </Card>
      )}

      {/* On Hold Info */}
      {phaseData.status?.toLowerCase() === "onhold" && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              On Hold Information
            </span>
          }
          style={{ borderRadius: 12, marginTop: 20, background: "#fffbe6", border: "1px solid #ffe58f" }}
          headStyle={{ borderBottom: "1px solid #ffe58f", minHeight: 40, background: "#fffbe6" }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Timeline
            items={[
              {
                color: "orange",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Hold Date</div>
                    <div style={{ color: "#666" }}>
                      {phaseData.phaseOnholdDate ? dayjs(phaseData.phaseOnholdDate).format("DD MMM YYYY") : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "green",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Expected Resolution</div>
                    <div style={{ color: "#666" }}>
                      {phaseData.phaseETAResolution ? dayjs(phaseData.phaseETAResolution).format("DD MMM YYYY") : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "blue",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Reason</div>
                    <div style={{ color: "#666" }}>{phaseData.phaseOnholdReason || "No reason provided"}</div>
                  </>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* Owner Reassignment Info */}
      {phaseData.originalPhaseOwnerId && phaseData.originalPhaseOwnerId !== phaseData.phaseOwnerId && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1890ff" }}>
              Owner Reassignment
            </span>
          }
          style={{ borderRadius: 12, marginTop: 20, background: "#e6f7ff", border: "1px solid #91d5ff" }}
          headStyle={{ borderBottom: "1px solid #91d5ff", minHeight: 40, background: "#e6f7ff" }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Descriptions column={1} size="small" labelStyle={{ fontWeight: 500, color: "#666" }}>
            <Descriptions.Item label="Original Owner">
              {phaseData.originalPhaseOwnerName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Reason">
              {phaseData.reassignedReason || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </div>
  );
};

export default PhaseQuickViewCard;
